/**
 * CollectionCard - Card display for a single collection
 *
 * Atomic level: Molecule
 * Displays collection with conduct score, record count, and last synced time
 *
 * Following GO UI Design System patterns
 */

import React, { useState } from 'react';
import { Clock3, Database, MoreVertical, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Timestamp } from '../../../ui/timestamp';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { getConductScoreColor } from './ConductScoreIndicator';
import { MonitoringDot } from './MonitoringDot';
import { DetectionPills } from './DetectionPills';
import { useRenameCollection, useDeleteCollection, useUpdateMonitoringStatus } from '../../../../hooks/useRecords';
import type { Collection } from '../../../../services/types';

interface CollectionCardProps {
  collection: Collection;
  onClick: () => void;
}

export const CollectionCard: React.FC<CollectionCardProps> = ({
  collection,
  onClick,
}) => {
  const hasScore = collection.averageSeverity !== null && collection.averageSeverity !== undefined;
  const scoreColor = hasScore ? getConductScoreColor(collection.averageSeverity!) : undefined;
  const displayScore = hasScore ? collection.averageSeverity!.toFixed(1) : null;

  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newName, setNewName] = useState(collection.name);

  const renameMutation = useRenameCollection();
  const deleteMutation = useDeleteCollection();
  const monitoringMutation = useUpdateMonitoringStatus();

  const currentMonitoringStatus = collection.monitoringStatus ?? 'active';

  const handleToggleMonitoring = () => {
    const nextStatus = currentMonitoringStatus === 'active' ? 'paused' : 'active';
    monitoringMutation.mutate(
      { collectionId: collection.id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(nextStatus === 'paused' ? 'Monitoring paused' : 'Monitoring resumed');
        },
        onError: (error) => {
          toast.error(`Failed to update monitoring: ${error.message}`);
        },
      }
    );
  };

  const handleRename = () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === collection.name) {
      setShowRenameDialog(false);
      return;
    }
    renameMutation.mutate(
      { collectionId: collection.id, name: trimmed },
      {
        onSuccess: () => {
          toast.success('Collection renamed');
          setShowRenameDialog(false);
        },
        onError: (error) => {
          toast.error(`Failed to rename: ${error.message}`);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(
      { collectionId: collection.id },
      {
        onSuccess: () => {
          toast.success('Collection deleted');
          setShowDeleteConfirm(false);
        },
        onError: (error) => {
          toast.error(`Failed to delete: ${error.message}`);
        },
      }
    );
  };

  const isDefault = collection.type === 'default';
  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <>
      <Card
        clickable
        role="button"
        tabIndex={0}
        onKeyDown={handleCardKeyDown}
        className="group overflow-hidden gap-0 border-border/80 bg-card p-0 shadow-[var(--shadow-card)] transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-[0_2px_8px_rgba(67,66,245,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--go-spring)]"
        onClick={onClick}
      >
        <CardHeader className="px-5 pt-5 pb-4">
          <div className="min-w-0 space-y-1">
            <CardTitle className="truncate">
              <span className="flex items-center gap-2">
                <MonitoringDot status={collection.monitoringStatus ?? 'active'} />
                <span className="truncate">{collection.name}</span>
              </span>
            </CardTitle>
            <CardDescription>
              {isDefault ? 'Default collection' : 'Custom collection'}
            </CardDescription>
            <DetectionPills
              eligibleComplaintsCount={collection.eligibleComplaintsCount ?? 0}
              vulnerableCustomersCount={collection.vulnerableCustomersCount ?? 0}
              showCounts
              className="pt-1"
            />
          </div>
          <CardAction className="flex items-center gap-2">
            <Badge variant="outline" className="rounded-full border-border/70 px-3 py-1 text-[11px] capitalize">
              {collection.type}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Collection actions for ${collection.name}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onSelect={() => {
                    setNewName(collection.name);
                    setShowRenameDialog(true);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={handleToggleMonitoring}
                  disabled={monitoringMutation.isPending}
                >
                  {currentMonitoringStatus === 'active' ? (
                    <>
                      <Pause className="mr-2 h-4 w-4" />
                      Pause monitoring
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Resume monitoring
                    </>
                  )}
                </DropdownMenuItem>
                {!isDefault && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardAction>
        </CardHeader>

        <CardContent className="space-y-5 px-5 pb-5 pt-0">
          <div className="min-h-[124px] rounded-xl border border-border/70 bg-card px-5 py-5">
            <div className="flex flex-col">
              <span
                className="text-[2.75rem] font-extrabold tabular-nums tracking-[-0.04em] sm:text-5xl"
                style={{ color: scoreColor || 'var(--muted-foreground)' }}
              >
                {displayScore ?? 'N/A'}
              </span>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                Conduct Score
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-5 text-[12px] text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Database className="h-4 w-4" />
              <span>{(collection.recordCount ?? 0).toLocaleString()} records</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock3 className="h-4 w-4" />
              <span>
                Updated{' '}
                <Timestamp value={collection.updatedAt} format="relative" fallback="Never" />
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Rename Collection</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collection name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRenameDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={!newName.trim() || newName.trim() === collection.name || renameMutation.isPending}
            >
              {renameMutation.isPending ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(next) => {
          if (!next) setShowDeleteConfirm(false);
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete collection?</DialogTitle>
            <DialogDescription>
              This permanently deletes &ldquo;{collection.name}&rdquo; and stops and
              removes any import monitor feeding it. Records already imported are
              preserved but unlinked from this collection. This can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
